import { ContentGroupFormatting } from "../pages/playground/types";

/**
 * Extracts the closing tag from an opening HTML tag string.
 * e.g. "<p>" -> "</p>", "<div class='x'>" -> "</div>", "" -> ""
 */
export function getClosingTag(tag: string): string {
  const m = tag.match(/^<\s*([a-zA-Z][a-zA-Z0-9]*)/);
  return m ? `</${m[1]}>` : "";
}

/**
 * Determines the visual bullet prefix to display for a single phrase.
 * Handles <ul><li>, <li>, plain characters, and "newline" sentinel.
 */
export function getBulletPrefixDisplay(formatting: ContentGroupFormatting): {
  text: string;
  isHtmlTag: boolean;
} {
  const bp = formatting.bulletPrefix;
  // V2: when bulletPrefix is "newline", the separator acts as per-line prefix
  if (
    bp === "newline" &&
    formatting.phraseSeparator &&
    formatting.phraseSeparator !== "space" &&
    formatting.phraseSeparator !== "newline"
  ) {
    const sepChar =
      formatting.phraseSeparator === "dash" ? "-" : formatting.phraseSeparator;
    return { text: sepChar, isHtmlTag: false };
  }
  if (!bp || bp === "newline") return { text: "", isHtmlTag: false };
  return { text: bp, isHtmlTag: false };
}

/**
 * Determines the wrap tag annotations to show around a group of phrases.
 * Returns opening/closing tags for the paragraph-level wrapper.
 */
export function getParagraphWrapDisplay(formatting: ContentGroupFormatting): {
  openingTag: string;
  closingTag: string;
} {
  const tag = formatting.paragraphWrapTag;
  if (!tag) return { openingTag: "", closingTag: "" };
  return { openingTag: tag, closingTag: getClosingTag(tag) };
}

/**
 * For bullet_points with bulletPrefix === "<ul><li>",
 * returns the <ul> opening and </ul> closing annotations.
 */
export function getListWrapDisplay(formatting: ContentGroupFormatting): {
  openingTag: string;
  closingTag: string;
} {
  if (formatting.bulletPrefix === "<ul><li>") {
    return { openingTag: "<ul>", closingTag: "</ul>" };
  }
  return { openingTag: "", closingTag: "" };
}

/**
 * Determines the per-phrase wrap tag annotations.
 * Returns opening/closing tags for the phrase-level wrapper (phraseWrapTag).
 */
export function getPhraseWrapDisplay(formatting: ContentGroupFormatting): {
  openingTag: string;
  closingTag: string;
} {
  const tag = formatting.phraseWrapTag;
  if (!tag) return { openingTag: "", closingTag: "" };
  return { openingTag: tag, closingTag: getClosingTag(tag) };
}

/**
 * Visual-only abbreviation of an HTML tag annotation for the editor.
 * `<strong>`/`</strong>` → `<b>`/`</b>` and `<em>`/`</em>` → `<i>`/`</i>`.
 * The stored data is unchanged; this only shrinks the on-screen marker so it
 * doesn't dominate the phrase row. Browsers render `<b>`/`<strong>` and
 * `<i>`/`<em>` identically, and stripConflictingTags treats them as equivalent.
 */
export function compactTagAnnotation(tag: string): string {
  const map: Record<string, string> = {
    "<strong>": "<b>",
    "</strong>": "</b>",
    "<em>": "<i>",
    "</em>": "</i>",
  };
  return map[tag] || tag;
}

/**
 * Whether phrases in this group should visually appear on separate lines.
 */
export function isNewLineMode(formatting: ContentGroupFormatting): boolean {
  return (
    formatting.bulletPrefix === "newline" ||
    formatting.phraseSeparator === "newline"
  );
}

/**
 * Whether this group has a phrase-level prefix (bulletPrefix that is not empty
 * and not "newline").
 */
export function hasPhrasePrefix(formatting: ContentGroupFormatting): boolean {
  const bp = formatting.bulletPrefix;
  if (
    bp === "newline" &&
    formatting.phraseSeparator &&
    formatting.phraseSeparator !== "space" &&
    formatting.phraseSeparator !== "newline"
  ) {
    return true;
  }
  return !!bp && bp !== "newline";
}

// ── Single source of truth: phrases → formatted string ───────────────
// Mirrors Python's format_phrases_with_config in master_file_sync.py

function wrapTag(text: string, tag: string): string {
  if (!tag) return text;
  const close = getClosingTag(tag);
  return close ? `${tag}${text}${close}` : `${tag}${text}`;
}

/**
 * Removes all occurrences of a specific HTML tag (by name) from `text`,
 * including its legacy/canonical equivalent: <b>≡<strong> and <i>≡<em>.
 * Used before wrapping to avoid nested identical tags when a wrap tag like
 * <strong> is combined with inline <strong>…</strong> (or legacy <b>) from
 * the rich-text editor.
 * e.g. stripConflictingTags("foo <b>bar</b>", "<strong>") → "foo bar".
 */
function stripConflictingTags(text: string, tag: string): string {
  if (!tag) return text;
  const m = tag.match(/^<\s*([a-zA-Z][a-zA-Z0-9]*)/);
  if (!m) return text;
  const name = m[1].toLowerCase();
  const equiv: Record<string, string> = { b: "strong", strong: "b", i: "em", em: "i" };
  const names = equiv[name] ? [name, equiv[name]] : [name];
  const re = new RegExp(`</?(?:${names.join("|")})\\b[^>]*>`, "gi");
  return text.replace(re, "");
}

/**
 * Formats an array of phrase strings into a single output string
 * according to ContentGroupFormatting rules.
 *
 * This is THE formatting function — used by PhraseGroup component,
 * settings preview, and should produce output identical to the Python backend.
 */
export function formatPhrases(
  phrases: string[],
  formatting: ContentGroupFormatting,
): string {
  if (!phrases || phrases.length === 0) return "";
  const f = formatting;

  // Strip inline tags that conflict with the configured wrap tags — e.g. if
  // phraseWrapTag is <b>, remove any <b>…</b> the editor inserted inline so
  // the final HTML doesn't end up with nested identical tags.
  if (f.phraseWrapTag || f.paragraphWrapTag || f.keyWrapTag) {
    phrases = phrases.map((p) => {
      let out = p;
      if (f.phraseWrapTag) out = stripConflictingTags(out, f.phraseWrapTag);
      if (f.paragraphWrapTag) out = stripConflictingTags(out, f.paragraphWrapTag);
      if (f.keyWrapTag) out = stripConflictingTags(out, f.keyWrapTag);
      return out;
    });
  }

  // Key-value mode: both phraseWrapTag and keyWrapTag wrap the KEY only.
  // keyWrapTag is the inner wrap, phraseWrapTag is the outer wrap on the key.
  // The value is always plain text.
  if (f.keyValueMode && phrases.length >= 2) {
    const pairs: string[] = [];
    for (let i = 0; i < phrases.length - 1; i += 2) {
      let key = phrases[i];
      if (f.keyWrapTag) key = wrapTag(key, f.keyWrapTag);
      if (f.phraseWrapTag) key = wrapTag(key, f.phraseWrapTag);
      pairs.push(`${key} ${phrases[i + 1]}`);
    }
    if (phrases.length % 2 !== 0) {
      let last = phrases[phrases.length - 1];
      if (f.keyWrapTag) last = wrapTag(last, f.keyWrapTag);
      if (f.phraseWrapTag) last = wrapTag(last, f.phraseWrapTag);
      pairs.push(last);
    }
    // Note: Python uses "<br>".join() for HTML export; here we use "\n" because
    // PhraseGroup's rendered mode converts \n → <br> in the rendering layer.
    let result = pairs.join("\n");
    if (f.paragraphWrapTag) result = wrapTag(result, f.paragraphWrapTag);
    return result;
  }

  // Wrap each phrase individually
  const wrapped = phrases.map((p) => wrapTag(p, f.phraseWrapTag));

  // Special HTML list handling (mirrors Python format_phrases_with_config)
  if (f.bulletPrefix === "<ul><li>") {
    const items = wrapped.map((p) => `<li>${p}</li>`);
    let result = "<ul>\n" + items.join("\n") + "\n</ul>";
    if (f.paragraphWrapTag) result = wrapTag(result, f.paragraphWrapTag);
    return result;
  }
  if (f.bulletPrefix === "<li>") {
    const items = wrapped.map((p) => `<li>${p}</li>`);
    let result = items.join("\n");
    if (f.paragraphWrapTag) result = wrapTag(result, f.paragraphWrapTag);
    return result;
  }

  // Determine separator — a custom bulletPrefix (e.g. "#", "•") implies newline mode
  const hasCustomBulletPrefix = !!f.bulletPrefix && f.bulletPrefix !== "newline";
  const useNewlines =
    f.bulletPrefix === "newline" || f.phraseSeparator === "newline" || hasCustomBulletPrefix;
  let joined: string;

  if (useNewlines) {
    let prefix = "";
    if (f.bulletPrefix && f.bulletPrefix !== "newline") {
      prefix = `${f.bulletPrefix} `;
    } else if (
      f.phraseSeparator &&
      f.phraseSeparator !== "space" &&
      f.phraseSeparator !== "newline"
    ) {
      const sepChar = f.phraseSeparator === "dash" ? "-" : f.phraseSeparator;
      prefix = `${sepChar} `;
    }
    joined = wrapped.map((p) => `${prefix}${p}`).join("\n");
  } else {
    let sep: string;
    switch (f.phraseSeparator) {
      case "space":
      case undefined:
      case "":
        sep = " ";
        break;
      case "dash":
        sep = " - ";
        break;
      default:
        sep = ` ${f.phraseSeparator} `;
        break;
    }
    joined = wrapped.join(sep).trim();
  }

  if (f.paragraphWrapTag) joined = wrapTag(joined, f.paragraphWrapTag);
  return joined;
}

/** Convenience: preview with example phrases (used in settings UI). */
export function generateFormattingPreview(
  formatting: ContentGroupFormatting,
  examplePhrases?: string[],
): string {
  const examples =
    examplePhrases ||
    (formatting.keyValueMode
      ? ["Key 1", "Value 1", "Key 2", "Value 2"]
      : ["Example phrase 1", "Example phrase 2", "Example phrase 3"]);
  return formatPhrases(examples, formatting);
}
